import { DownloadManyDocumentsRequest, CPS_NOTIFICATION_TYPE } from '@cs-cms/types';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useGlobalizationContext } from '../../contexts/GlobalizationContext';
import ServicesContext from '../../contexts/ServicesContext';
import { mutateDocumentsKeys } from '../../mutationKeys/documents';
import { useTranslation } from '../globalization/useTranslation';
import { useNotificationContext } from '../useNotificationContext';

interface UseDownloadManyOptions {
  zipName: string;
}

export function useDownloadMany(
  options: UseDownloadManyOptions
): UseMutationResult<void, unknown, { dto: DownloadManyDocumentsRequest }> {
  const servicesCtx = useContext(ServicesContext);
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { addCenterNotification, showToast } = useNotificationContext();

  const downloadMany = (data: { dto: DownloadManyDocumentsRequest }): Promise<void> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.downloadManyDocuments(data.dto, options.zipName);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.downloadManyDocuments,
    mutationFn: (data: { dto: DownloadManyDocumentsRequest }) => downloadMany(data),
    onSuccess: (data, variables) => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentSuccessTitle'),
        message: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentSuccessMessage', {
          count: variables.dto.documents.length
        }),
        timestamp: new Date(),
        type: 'success'
      });

      showToast({
        type: CPS_NOTIFICATION_TYPE.SUCCESS,
        message: translate('custom.toolbar.actions.download.downloadSuccess')
      });
    },
    onError: () => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentFailedMessage'),
        timestamp: new Date(),
        type: 'error'
      });

      showToast({
        type: CPS_NOTIFICATION_TYPE.ERROR,
        message: translate('custom.toolbar.actions.download.downloadFailed')
      });
    }
  });
}
